import { useEffect, type FC } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate
} from "react-router-dom";
import NewConversationView from "./NewConversationView";
import ConversationDetailView from "./ConversationDetailView";
import UpdatesView from "./UpdatesView";
import { useBlocNext } from "@blac/react";
import UserPreferencesCubit, {
  UserPreferenceKeys
} from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import ChatDrawerLayout from "./ChatDrawerLayout";
import LeapChatRouterCubit from "./state/LeapChatRouterCubit";
import { ChatTourVariant } from "./state/chatTour";
import { DRAWER_DESKTOP_BREAKPOINT } from "./leapChat.styles";
import { useMediaQuery } from "@react-hooks-library/core";

const ChatIndexRedirect: FC = () => {
  const [
    {
      checking,
      autoSelectPath,
      lastChatPath,
      showAnnouncement,
      conversationHistoryId
    },
    {
      resolveIndexRedirect,
      showSophieAnnouncement,
      startTourFromSource,
      markConversationOnboarded
    }
  ] = useBlocNext(LeapChatRouterCubit);
  const navigate = useNavigate();
  const isMobile = useMediaQuery(`(max-width: ${DRAWER_DESKTOP_BREAKPOINT}px)`);

  useEffect(() => {
    if (isMobile) {
      resolveIndexRedirect();
    }
  }, [resolveIndexRedirect, conversationHistoryId, isMobile]);

  const isOnNewConversationView =
    !checking &&
    !autoSelectPath &&
    !(lastChatPath && lastChatPath !== "/app/chat");

  useEffect(() => {
    if (isOnNewConversationView && showAnnouncement && isMobile) {
      showSophieAnnouncement(
        () => {
          markConversationOnboarded();
          startTourFromSource(ChatTourVariant.OnlyOldMessages, navigate);
        },
        () => {
          markConversationOnboarded();
        }
      );
    }
  }, [
    isOnNewConversationView,
    showAnnouncement,
    showSophieAnnouncement,
    startTourFromSource,
    markConversationOnboarded,
    navigate,
    isMobile
  ]);

  if (checking && isMobile) {
    return null;
  }

  if (lastChatPath && lastChatPath !== "/app/chat") {
    return <Navigate to={lastChatPath} replace />;
  }

  if (autoSelectPath) {
    return <Navigate to={autoSelectPath} replace />;
  }

  return <NewConversationView />;
};

const LeapChatRouter: FC = () => {
  const [, { initTourGuide: onMount, trackPath, setConversationHistoryId }] =
    useBlocNext(LeapChatRouterCubit);
  const [preferencesState] = useBlocNext(UserPreferencesCubit);
  const location = useLocation();

  useEffect(() => {
    return onMount();
  }, [onMount]);

  useEffect(() => {
    trackPath(location.pathname);
  }, [trackPath, location.pathname]);

  const conversationHistoryId =
    preferencesState[UserPreferenceKeys.previousMessagesConversationId];

  useEffect(() => {
    if (conversationHistoryId) {
      setConversationHistoryId(conversationHistoryId);
    }
  }, [conversationHistoryId, setConversationHistoryId]);

  return (
    <ChatDrawerLayout>
      <Routes>
        <Route index element={<ChatIndexRedirect />} />
        <Route path="new" element={<NewConversationView />} />
        <Route path="updates" element={<UpdatesView />} />
        <Route path="conversation/:id" element={<ConversationDetailView />} />
        <Route path="*" element={<Navigate to="/app/chat" replace />} />
      </Routes>
    </ChatDrawerLayout>
  );
};

export default LeapChatRouter;
